import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../Contexts/AuthContext';
import {
    collection,
    query,
    where,
    getDocs,
    orderBy
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useNavigate } from 'react-router-dom';
import {
    UserGroupIcon,
    CalendarIcon,
    EyeIcon,
    BookOpenIcon,
    GlobeIcon,
    ChatIcon,
    SearchIcon
} from '@heroicons/react/outline';
import { addDoc, serverTimestamp } from 'firebase/firestore';

const MentorOverview = () => {
    const { currentUser, userData } = useAuth();
    const [teams, setTeams] = useState([]);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [teamsPerPage, setTeamsPerPage] = useState(10);
    const navigate = useNavigate();
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcasting, setBroadcasting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [allResources, setAllResources] = useState([]);

    useEffect(() => {
        const fetchMentorTeams = async () => {
            try {
                setLoading(true);
                const teamsRef = collection(db, 'hackathonTeams');

                let q;
                if (userData?.role === 'Administrator') {
                    // Administrators can see all teams
                    q = query(
                        teamsRef,
                        orderBy('createdAt', 'desc')
                    );
                } else {
                    // Mentors can only see their assigned teams
                    q = query(
                        teamsRef,
                        where('mentorId', '==', currentUser.uid),
                        orderBy('createdAt', 'desc')
                    );
                }

                const querySnapshot = await getDocs(q);

                const teamsData = [];
                querySnapshot.forEach((doc) => {
                    teamsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                console.log('Fetched mentor teams:', teamsData);

                setTeams(teamsData);
                setFilteredTeams(teamsData);
            } catch (error) {
                console.error('Error fetching mentor teams:', error);
            } finally {
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchMentorTeams();
        }
    }, [currentUser, userData?.role]);

    // Fetch all resources from resources collection
    useEffect(() => {
        const fetchResources = async () => {
            try {
                const resourcesRef = collection(db, 'resources');

                let resourcesQuery;
                if ((userData?.role && ['admin', 'Administrator', 'Admin'].includes(userData.role))) {
                    // Administrators can see all resources
                    resourcesQuery = resourcesRef;
                } else {
                    // Mentors can see their own resources AND admin resources
                    const mentorResourcesQuery = query(resourcesRef, where('mentorId', '==', currentUser.uid));
                    const adminResourcesQuery = query(resourcesRef, where('isAdminResource', '==', true));

                    // Fetch both mentor and admin resources
                    const [mentorSnapshot, adminSnapshot] = await Promise.all([
                        getDocs(mentorResourcesQuery),
                        getDocs(adminResourcesQuery)
                    ]);

                    const mentorResources = mentorSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    const adminResources = adminSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Combine and deduplicate by id
                    const combinedResources = [...mentorResources, ...adminResources];
                    const uniqueResources = Array.from(
                        new Map(combinedResources.map(resource => [resource.id, resource])).values()
                    );

                    setAllResources(uniqueResources);
                    console.log('Fetched mentor + admin resources:', uniqueResources);
                    return;
                }

                const resourcesSnapshot = await getDocs(resourcesQuery);

                const resourcesData = resourcesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setAllResources(resourcesData);
                console.log('Fetched all resources:', resourcesData);
            } catch (error) {
                console.error('Error fetching resources:', error);
            }
        };

        if (currentUser) {
            fetchResources();
        }
    }, [currentUser, userData?.role]);

    // Search effect with debouncing
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredTeams(teams);
            setCurrentPage(1);
            return;
        }

        setSearchLoading(true);
        const delayedSearch = setTimeout(() => {
            searchTeams(searchTerm);
            setCurrentPage(1);
        }, 300); // 300ms debounce for search

        return () => {
            clearTimeout(delayedSearch);
            setSearchLoading(false);
        };
    }, [searchTerm, teams]);

    // Reset to page 1 when teamsPerPage changes
    useEffect(() => {
        setCurrentPage(1);
    }, [teamsPerPage]);

    const searchTeams = useCallback((searchQuery) => {
        if (!searchQuery.trim()) {
            setFilteredTeams(teams);
            setSearchLoading(false);
            return;
        }

        const searchLower = searchQuery.toLowerCase().trim();
        const results = teams.filter(team => {
            const teamLeader = getTeamLeader(team);
            return (
                team.name?.toLowerCase().includes(searchLower) ||
                team.description?.toLowerCase().includes(searchLower) ||
                teamLeader?.name?.toLowerCase().includes(searchLower) ||
                team.members?.some(member =>
                    member.name?.toLowerCase().includes(searchLower) ||
                    member.email?.toLowerCase().includes(searchLower)
                )
            );
        });

        setFilteredTeams(results);
        setSearchLoading(false);
    }, [teams]);

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getMentorResources = () => {
        if (!allResources || allResources.length === 0) return 0;
        return allResources.filter(resource =>
            resource.mentorId === currentUser.uid && resource.isAdminResource !== true
        ).length;
    };

    const getAdminResources = () => {
        if (!allResources || allResources.length === 0) return 0;
        return allResources.filter(resource => resource.isAdminResource === true).length;
    };


    const getTotalMembers = () => {
        return teams.reduce((sum, team) => sum + (team.members?.length || 0), 0);
    };

    const getTeamResources = (team) => {
        if (!allResources || allResources.length === 0) return [];

        // Filter resources for this team
        const teamResources = allResources.filter(resource => {
            // Admin resources are available to everyone
            if (resource.isAdminResource === true) {
                return true;
            }

            // For other resources, check if team is in allowedTeams
            return resource.mentorId === team.mentorId && (resource.visibility === 'public' || (resource.allowedTeams && resource.allowedTeams.includes(team.id)));
        });

        return teamResources;
    };

    const getTeamResourceCount = (team) => {
        return getTeamResources(team).length;
    };

    const getTeamLeader = (team) => {
        if (!team.createdBy || !team.members) return null;
        return team.members.find(member => member.id === team.createdBy);
    };

    const handleViewTeam = (team) => {
        const teamResources = getTeamResources(team);
        navigate(`/eventdashboard/mentor-team-details/${team.id}`, {
            state: {
                team,
                resources: teamResources
            }
        });
    };

    const handleManageAllTeams = () => {
        navigate('/eventdashboard/all-teams-resources', {
            state: { teams, resources: allResources }
        });
    };

    const handleTeamsPerPageChange = (newTeamsPerPage) => {
        setTeamsPerPage(newTeamsPerPage);
        setCurrentPage(1);
    };

    // Broadcast send handler
    const handleSendBroadcast = async () => {
        if (!broadcastMessage.trim() || teams.length === 0) return;
        setBroadcasting(true);
        setNotification(null);

        try {
            const sendPromises = teams.map(async (team) => {
                const messagesRef = collection(db, 'hackathonTeams', team.id, 'messages');
                return await addDoc(messagesRef, {
                    text: broadcastMessage.trim(),
                    senderId: currentUser?.uid || 'system',
                    senderName: currentUser?.displayName || 'System',
                    senderEmail: currentUser?.email || '',
                    senderPhotoURL: currentUser?.photoURL || '',
                    userRole: userData?.role || 'Administrator',
                    timestamp: serverTimestamp(),
                    createdAt: new Date().toISOString()
                });
            });

            await Promise.all(sendPromises);

            setNotification(userData?.role && ['Administrator', 'admin'].includes(userData.role) ? 'Message sent to all team chats.' : 'Message sent to your teams.');
            setBroadcastMessage('');
            setShowBroadcastModal(false);
        } catch (error) {
            console.error('Error broadcasting message:', error);
            setNotification('Failed to send messages. Please try again.');
        } finally {
            setBroadcasting(false);
            setTimeout(() => setNotification(null), 6000);
        }
    };

    // Pagination logic
    const indexOfLastTeam = currentPage * teamsPerPage;
    const indexOfFirstTeam = indexOfLastTeam - teamsPerPage;
    const currentTeams = filteredTeams.slice(indexOfFirstTeam, indexOfLastTeam);
    const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            {userData?.role === 'Administrator' ? 'Teams Overview' : 'Mentor Dashboard'}
                        </h1>
                        <p className="text-gray-600">
                            {userData?.role === 'Administrator'
                                ? 'Manage all teams and track overall progress'
                                : 'Manage your assigned teams and track mentoring progress'
                            }
                        </p>
                    </div>
                    <div className='gap-3 flex flex-col sm:flex-row'>
                        {teams.length > 0 && (
                            <button
                                onClick={handleManageAllTeams}
                                className="flex items-center gap-2 px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <GlobeIcon className="w-5 h-5" />
                                Manage All Teams Resources
                            </button>
                        )}
                        {teams.length > 0 && (
                            <button
                                onClick={() => setShowBroadcastModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <ChatIcon className="w-5 h-5" />
                                Send Message to Teams
                            </button>
                        )}
                    </div>

                    {/* Notification card */}
                    {notification && (
                        <div className="mt-4 p-3 rounded bg-green-50 border border-green-200 text-green-800">
                            {notification}
                        </div>
                    )}

                    {/* Broadcast Modal */}
                    {showBroadcastModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg w-full max-w-2xl p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold">
                                        {userData?.role && ['Administrator', 'admin'].includes(userData.role)
                                            ? 'Send message to all team chats'
                                            : 'Send message to my teams'
                                        }
                                    </h3>
                                    <button className="text-gray-500" onClick={() => setShowBroadcastModal(false)}>Close</button>
                                </div>

                                <textarea
                                    value={broadcastMessage}
                                    onChange={(e) => setBroadcastMessage(e.target.value)}
                                    rows={6}
                                    className="w-full p-3 border border-gray-300 rounded mb-4"
                                    placeholder="Type the message to send to selected teams..."
                                />

                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowBroadcastModal(false)}
                                        className="px-4 py-2 rounded bg-white border border-gray-300"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSendBroadcast}
                                        disabled={broadcasting || !broadcastMessage.trim()}
                                        className={`px-4 py-2 rounded text-white ${broadcasting || !broadcastMessage.trim() ? 'bg-gray-300' : 'bg-custom-blue-1 hover:bg-opacity-90'}`}
                                    >
                                        {broadcasting ? 'Sending...' : 'Send Message'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">
                                {userData?.role === 'Administrator' ? 'Total Teams' : 'My Teams'}
                            </p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">{teams.length}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50">
                            <UserGroupIcon className="h-8 w-8 text-custom-blue-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">
                                {userData?.role === 'Administrator' ? 'All Resources' : 'My Resources'}
                            </p>
                            <p className="text-3xl font-bold text-custom-green-1 mt-2">
                                {userData?.role === 'Administrator' ? allResources.length : getMentorResources()}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50">
                            <BookOpenIcon className="h-8 w-8 text-custom-green-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Admin Resources</p>
                            <p className="text-3xl font-bold text-purple-600 mt-2">
                                {getAdminResources()}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-50">
                            <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Members</p>
                            <p className="text-3xl font-bold text-orange-500 mt-2">
                                {getTotalMembers()}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50">
                            <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            {teams.length > 0 && (
                <div className="mb-6">
                    <div className="relative max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search teams by name, description, or members..."
                            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                        />
                        {searchLoading && (
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1"></div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Results Summary and Items Per Page */}
            {teams.length > 0 && (
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <p className="text-sm text-gray-600">
                            {searchLoading ? (
                                <span className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1 mr-2"></div>
                                    Searching...
                                </span>
                            ) : (
                                <>
                                    Showing {currentTeams.length > 0 ? ((currentPage - 1) * teamsPerPage) + 1 : 0} to{' '}
                                    {Math.min(currentPage * teamsPerPage, Math.min(filteredTeams.length, ((currentPage - 1) * teamsPerPage) + currentTeams.length))} of {filteredTeams.length} teams
                                    {searchTerm.trim() && ` (filtered from ${teams.length} total)`}
                                </>
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                            Show:
                        </label>
                        <select
                            id="itemsPerPage"
                            value={teamsPerPage}
                            onChange={(e) => handleTeamsPerPageChange(Number(e.target.value))}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                            disabled={searchLoading}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-600">per page</span>
                    </div>
                </div>
            )}

            {teams.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {userData?.role === 'Administrator' ? 'No Teams Found' : 'No Teams Assigned'}
                    </h3>
                    <p className="text-gray-600">
                        {userData?.role === 'Administrator'
                            ? 'No teams have been created yet. Teams will appear here once they are created.'
                            : 'You haven\'t been assigned to any teams yet. Check back later or contact the administrator.'
                        }
                    </p>
                </div>
            ) : (
                <>
                    {/* Teams Table */}
                    <div className="bg-white shadow-sm overflow-hidden rounded-lg border">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Team Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Members
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Resources
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {searchLoading ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-blue-1 mr-2"></div>
                                                    Searching teams...
                                                </div>
                                            </td>
                                        </tr>
                                    ) : currentTeams.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                                {searchTerm.trim() ? 'No teams found matching your search' : 'No teams found'}
                                            </td>
                                        </tr>
                                    ) : (
                                        currentTeams.map((team) => {
                                            const teamLeader = getTeamLeader(team);
                                            return (
                                                <tr key={team.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {team.name}
                                                            </div>
                                                            <div className="text-sm text-gray-500 max-w-xs truncate">
                                                                {team.description}
                                                            </div>
                                                            {teamLeader && (
                                                                <div className="text-xs text-gray-400 mt-1">
                                                                    Led by {teamLeader.name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                                                            <span className="text-sm text-gray-900">
                                                                {team.members?.length || 0} members
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <BookOpenIcon className="h-5 w-5 text-gray-400 mr-2" />
                                                            <span className="text-sm text-gray-900">
                                                                {getTeamResourceCount(team)} resources
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <div className="flex items-center">
                                                            <CalendarIcon className="h-4 w-4 mr-1" />
                                                            {formatDate(team.createdAt)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => handleViewTeam(team)}
                                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-1" />
                                                            Manage Team
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                        let pageNumber;
                                        if (totalPages <= 7) {
                                            pageNumber = i + 1;
                                        } else if (currentPage <= 4) {
                                            pageNumber = i + 1;
                                        } else if (currentPage >= totalPages - 3) {
                                            pageNumber = totalPages - 6 + i;
                                        } else {
                                            pageNumber = currentPage - 3 + i;
                                        }

                                        if (pageNumber < 1 || pageNumber > totalPages) return null;

                                        return (
                                            <button
                                                key={pageNumber}
                                                onClick={() => handlePageChange(pageNumber)}
                                                className={`px-3 py-2 text-sm font-medium rounded-md ${pageNumber === currentPage
                                                    ? 'bg-custom-blue-1 text-white'
                                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>

                            <div className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MentorOverview;